import React from "react";
import { Button, Heading } from "@flixbus/honeycomb-react";
import { Icon, IconInfoSolid } from "@flixbus/honeycomb-icons-react";
import { pluralize } from "@/utils/pluralize";
import styles from "./EmptyInputsState.module.scss";

interface EmptyInputsStateProps {
  reason: "noFormula" | "noFormulaVariables";
  onEnterFormulaMode: () => void;
  noDimensionValues?: boolean;
  dimensionTypeName?: string;
  onSelectValues?: () => void;
}

const copyByReason: Record<
  EmptyInputsStateProps["reason"],
  { heading: string; buttonLabel: string }
> = {
  noFormula: {
    heading:
      "This bucket doesn't have a formula yet. Add one to see which inputs you need to fill in.",
    buttonLabel: "Add formula",
  },
  noFormulaVariables: {
    heading:
      "This bucket's formula doesn't reference any variables, so there's nothing to fill in here.",
    buttonLabel: "Edit formula",
  },
};

const EmptyInputsState: React.FC<EmptyInputsStateProps> = ({
  reason,
  onEnterFormulaMode,
  noDimensionValues = false,
  dimensionTypeName,
  onSelectValues,
}) => {
  const { heading, buttonLabel } = copyByReason[reason];
  const dimensionLabel = dimensionTypeName?.toLowerCase() ?? "dimension value";
  const selectValuesLabel = dimensionTypeName
    ? `Select ${pluralize(dimensionLabel)}`
    : "Select values";
  return (
    <div className={styles.container}>
      <Icon InlineIcon={IconInfoSolid} size={6} extraClasses={styles.icon} />
      <Heading size={4} flushSpace>
        {noDimensionValues
          ? `Select at least one ${dimensionLabel} before adding a formula.`
          : heading}
      </Heading>
      <div className={styles.actions}>
        <Button
          appearance="primary"
          onClick={onEnterFormulaMode}
          disabled={noDimensionValues}
        >
          {buttonLabel}
        </Button>
        {noDimensionValues && onSelectValues && (
          <Button appearance="secondary" onClick={onSelectValues}>
            {selectValuesLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

export default React.memo(EmptyInputsState);
